// SUB-OFFICE 7 — FORM 44-S, STATIONARY COMPOSURE CERTIFICATION (the CIVIC
// slot). Stand on the mark and hold. The building leans on you — a stare, a
// converging lamp, a slammed tray, a false all-clear, and a telephone that
// rings and asks to be answered. Answering it ends the test, DENIED. The route
// samples at 250ms through the hold; the server recomputes drift/excursions
// from the raw route against the hold-start position and correlates them with
// the seeded beat script. The client records only raw stand/beat/press events.

import {
  CircleGeometry, Color, Group, Mesh, MeshBasicMaterial, RingGeometry,
} from 'three';
import { anchorCoord44S, type Form44SSpec } from '../../shared/physspec';
import { PAL } from '../palette';
import { patchMaterial } from '../render/scene';
import { holdSlamButton, printLayer } from '../ui/chrome';
import { PhysLogRecorder } from './log';
import type { BenchDeps } from './bench';
import type { BehaviorLog } from '../../shared/physlog';

const WP2WORLD = (wx: number, wy: number): [number, number] => [31 - wx, 17 - wy];
const WORLD2WP = (x: number, z: number): [number, number] => [31 - x, 17 - z];

const BEAT_LINE: Record<string, string> = {
  phone: 'A TELEPHONE RINGS. IT IS FOR YOU. IT IS ALWAYS FOR YOU.',
  ghost_stare: 'A PETITIONER ACROSS THE HALL IS STARING. DO NOT MEET IT.',
  lamp_converge: 'THE EYE-LAMPS TURN, SLOWLY, TOWARD THE MARK.',
  tray_slam: 'A TRAY SLAMS SOMEWHERE BEHIND YOU. DO NOT TURN.',
  false_release: 'COMPOSURE ACHIEVED. YOU MAY STEP OFF. (YOU MAY NOT. THIS IS A TEST.)',
  flicker: 'THE LIGHTS STUTTER. HOLD.',
  whisper: 'A WHISPER GIVES YOUR DESIGNATION. IGNORE IT.',
  ring_cluster: 'THREE TELEPHONES NOW. A CHOIR OF OBLIGATION.',
};

export function runComposure(
  deps: BenchDeps,
  taskId: string,
  spec: Form44SSpec
): Promise<BehaviorLog | null> {
  return new Promise((resolve) => {
    const rec = new PhysLogRecorder('FORM44S', taskId);
    const group = new Group();
    deps.sceneMgr.scene.add(group);
    const [awx, awy] = anchorCoord44S(spec.anchor);
    const [ax, az] = WP2WORLD(awx, awy);

    const ringMat = new MeshBasicMaterial({ color: new Color(PAL.amber).convertSRGBToLinear(), transparent: true, opacity: 0.8, depthWrite: false });
    patchMaterial(ringMat, { dimExempt: true });
    const ring = new Mesh(new RingGeometry(spec.ring_r, spec.ring_r + 0.12, 28), ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(ax, 0.03, az);
    group.add(ring);
    const dot = new Mesh(new CircleGeometry(0.12, 16), ringMat);
    dot.rotation.x = -Math.PI / 2;
    dot.position.set(ax, 0.032, az);
    group.add(dot);

    deps.bus.emit('chyron', 'STATIONARY COMPOSURE. STAND ON THE MARK. HOLD. WHATEVER RINGS, LET IT RING.');
    deps.bus.emit('context', `STEP ONTO THE AMBER MARK AND STOP. HOLD FOR ${spec.duration_s}s.`);

    const layer = printLayer();
    let holding = false;
    let holdStartMs = 0;
    let startX = 0;
    let startZ = 0;
    let over = false;
    let raf = 0;
    let firedBeats = 0;
    let inExcursion = false;
    let excursionAt = 0;
    let lastDrift = 0;
    let phonePrompt: HTMLElement | null = null;

    const cleanup = (): void => {
      over = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey);
      rec.stopRoute();
      if (phonePrompt) phonePrompt.remove();
      deps.sceneMgr.scene.remove(group);
    };
    const submit = (ok: boolean): void => {
      const built = rec.build(true);
      if (built.valid) resolve(built.log);
      else {
        deps.bus.emit('toast', `The composure log did not assemble (${built.reason}).`);
        resolve(ok ? null : null);
      }
    };
    const endHold = (ok: boolean): void => {
      if (over) return;
      rec.ev('hold_end', { ok });
      cleanup();
      deps.bus.emit('chyron', ok
        ? 'THE HOLD IS RELEASED BY THE CLOCK, NOT BY YOU. COMPOSURE IS FILED.'
        : 'YOU MOVED. THE MARK REMEMBERS EXACTLY HOW MUCH.');
      submit(ok);
    };
    const abandon = (): void => {
      if (over) return;
      if (holding) { rec.ev('hold_end', { ok: false }); cleanup(); submit(false); }
      else { cleanup(); resolve(null); }
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') abandon(); };
    window.addEventListener('keydown', onKey);

    const ringPhone = (target: string): void => {
      rec.ev('prompt_shown', { target });
      if (phonePrompt) phonePrompt.remove();
      const p = document.createElement('div');
      p.style.cssText = 'position:absolute;left:50%;bottom:70px;transform:translateX(-50%);background:var(--paper);border:2px solid var(--stamp-red);padding:10px 14px;text-align:center;box-shadow:0 8px 24px rgba(28,26,23,.5)';
      p.innerHTML = '<div class="compose-note" style="color:var(--stamp-red)">THE TELEPHONE IS RINGING</div>';
      const answer = holdSlamButton('ANSWER IT', () => {
        rec.ev('interact_press', { target });
        deps.bus.emit('audio:buzz');
        endHold(false);
      }, { className: 'red' });
      p.appendChild(answer);
      layer.appendChild(p);
      phonePrompt = p;
      deps.bus.emit('audio:tick');
      // it stops ringing after a while (the temptation passes)
      window.setTimeout(() => { if (phonePrompt === p) { p.remove(); phonePrompt = null; } }, 6000);
    };

    const frame = (): void => {
      if (over) return;
      raf = requestAnimationFrame(frame);
      const px = deps.player.pos.x;
      const pz = deps.player.pos.z;
      if (!holding) {
        const d = Math.hypot(px - ax, pz - az);
        if (d <= spec.ring_r + 0.6) {
          holding = true;
          holdStartMs = rec.now();
          startX = px;
          startZ = pz;
          (ringMat as MeshBasicMaterial).color = new Color(PAL.gas).convertSRGBToLinear();
          rec.ev('hold_start', {});
          rec.startRoute(deps.player, 250, WORLD2WP);
          deps.bus.emit('context', 'HOLD. LET THE BUILDING TRY YOU.');
        }
        return;
      }
      const elapsed = rec.now() - holdStartMs;
      // fire beats on schedule
      while (firedBeats < spec.beats.length && spec.beats[firedBeats].t_ms <= elapsed) {
        const b = spec.beats[firedBeats];
        rec.ev('beat_fired', { beat_id: b.id });
        deps.bus.emit('chyron', BEAT_LINE[b.id] || 'THE HALL SHIFTS.');
        if (b.id === 'phone' || b.id === 'ring_cluster') ringPhone(b.id);
        else if (b.id === 'flicker' || b.id === 'tray_slam') deps.bus.emit('audio:thunk');
        firedBeats++;
      }
      // drift / excursion (display-only; server recomputes from the route)
      const drift = Math.hypot(px - startX, pz - startZ);
      if (Math.abs(drift - lastDrift) > 0.08) {
        rec.ev('drift', { d: Math.round(drift * 100) / 100 });
        lastDrift = drift;
      }
      if (!inExcursion && drift > spec.ring_r) {
        inExcursion = true;
        excursionAt = rec.now();
        rec.ev('excursion', { d: Math.round(drift * 100) / 100, near: nearestBeat(spec, elapsed) });
        deps.bus.emit('audio:buzz');
      } else if (inExcursion && drift <= spec.ring_r) {
        inExcursion = false;
        rec.ev('reentry', { ms: rec.now() - excursionAt });
      }
      if (elapsed >= spec.duration_s * 1000) endHold(true);
    };
    raf = requestAnimationFrame(frame);
  });
}

function nearestBeat(spec: Form44SSpec, elapsed: number): string {
  let best = '';
  let bestD = Infinity;
  for (const b of spec.beats) {
    const d = Math.abs(b.t_ms - elapsed);
    if (d < bestD) { bestD = d; best = b.id; }
  }
  return best;
}
